import {
  useCallback,
  useReducer,
  useEffect,
  useState,
  ChangeEvent,
  MouseEvent,
} from "react";
import { ProfessionalTransaction } from "src/types/snugtotal";
import {
  useV3GenericGetData,
  useV3GenericGetDataWithPagination,
} from "src/utils/snug/estateAppApi";
import { GenericPath } from "src/utils/snug/estateAppApi";
import useUserDataProvider from "src/contexts/snug/userData-context";

// Define types
type SortDir = "asc" | "desc";

interface Filters {
  query?: string;
  advisor_ud_id?: string;
  transaction_types?: string; // comma-separated
  created_after?: string;     // YYYY-MM-DD
  created_before?: string;    // YYYY-MM-DD
}

interface ProTransactionsSearchState {
  filters: Filters;
  page: number;
  rowsPerPage: number;
  sortBy?: string;
  sortDir?: SortDir;
}

export interface TransactionOverview {
  net_payout_to_professional: number;
  transaction_count: number;
}

export interface AvailableCredits {
  available_credit: number;
}

// Purchase types the Transactions table and its CSV show under "All types".
// Listed explicitly so CREDIT rows stay out of the table.
export const DEFAULT_TRANSACTION_TYPES = "WILL,TRUST,JOINT_TRUST,QUITCLAIM_DEED";

const useProtransactionsearch = () => {
  const [proTxnSearchAndPaginationState, setProTxnSearchAndPaginationState] =
    useState<ProTransactionsSearchState>({
      filters: {
        query: undefined,
      },
      page: 0,
      rowsPerPage: 25,
      sortBy: "created_at",
      sortDir: "desc",
    });

  const handleFiltersChange = useCallback((filters: Filters): void => {
    setProTxnSearchAndPaginationState((prevState) => ({
      ...prevState,
      filters,
    }));
  }, []);

  const handleSortChange = useCallback(
    (sortBy: string, sortDir: SortDir): void => {
      setProTxnSearchAndPaginationState((prevState) => ({
        ...prevState,
        sortBy,
        sortDir,
      }));
    },
    []
  );

  const handlePageChange = useCallback(
    (event: MouseEvent<HTMLButtonElement> | null, page: number): void => {
      setProTxnSearchAndPaginationState((prevState) => ({
        ...prevState,
        page,
      }));
    },
    []
  );

  const handleRowsPerPageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setProTxnSearchAndPaginationState((prevState) => ({
        ...prevState,
        rowsPerPage: parseInt(event.target.value, 10),
      }));
    },
    []
  );

  return {
    handleFiltersChange,
    handleSortChange,
    handlePageChange,
    handleRowsPerPageChange,
    proTxnSearchAndPaginationState,
    setProTxnSearchAndPaginationState,
  };
};

const useCreditTransactionsSearch = () => {
  const [creditTxnSearchAndPaginationState, setProTxnSearchAndPaginationState] =
    useState<ProTransactionsSearchState>({
      filters: {
        query: undefined,
      },
      page: 0,
      rowsPerPage: 25,
      sortBy: "created_at",
      sortDir: "desc",
    });

  const handleFiltersChange = useCallback((filters: Filters): void => {
    setProTxnSearchAndPaginationState((prevState) => ({
      ...prevState,
      filters,
    }));
  }, []);

  const handleSortChange = useCallback(
    (sortBy: string, sortDir: SortDir): void => {
      setProTxnSearchAndPaginationState((prevState) => ({
        ...prevState,
        sortBy,
        sortDir,
      }));
    },
    []
  );

  const handlePageChange = useCallback(
    (event: MouseEvent<HTMLButtonElement> | null, page: number): void => {
      setProTxnSearchAndPaginationState((prevState) => ({
        ...prevState,
        page,
      }));
    },
    []
  );

  const handleRowsPerPageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setProTxnSearchAndPaginationState((prevState) => ({
        ...prevState,
        rowsPerPage: parseInt(event.target.value, 10),
      }));
    },
    []
  );

  return {
    handleFiltersChange,
    handleSortChange,
    handlePageChange,
    handleRowsPerPageChange,
    creditTxnSearchAndPaginationState,
  };
};

// Define actions for the reducer
type TransactionsStoreAction =
  | { type: "SET_TRANSACTIONS"; payload: ProfessionalTransaction[] }
  | { type: "SET_TRANSACTIONS_LOADING"; payload: boolean }
  | { type: "SET_TRANSACTIONS_COUNT"; payload: number }
  | { type: "SET_CREDITS"; payload: ProfessionalTransaction[] }
  | { type: "SET_CREDITS_LOADING"; payload: boolean }
  | { type: "SET_CREDITS_COUNT"; payload: number }
  | { type: "SET_TRANSACTION_OVERVIEW"; payload: TransactionOverview }
  | { type: "SET_TRANSACTION_OVERVIEW_LOADING"; payload: boolean }
  | { type: "SET_AVAILABLE_CREDITS"; payload: AvailableCredits }
  | { type: "SET_AVAILABLE_CREDITS_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: any };

interface TransactionsStoreState {
  transactions: ProfessionalTransaction[];
  transactionsLoading: boolean;
  transactionsCount: number;
  credits: ProfessionalTransaction[];
  creditsLoading: boolean;
  creditsCount: number;
  transactionOverview: TransactionOverview | null;
  transactionOverviewLoading: boolean;
  availableCredits: AvailableCredits | null;
  availableCreditsLoading: boolean;
  error: string | null;
}

// Define the reducer function
const transactionsStoreReducer = (
  state: TransactionsStoreState,
  action: TransactionsStoreAction
): TransactionsStoreState => {
  switch (action.type) {
    case "SET_TRANSACTIONS":
      return { ...state, transactions: action.payload };
    case "SET_TRANSACTIONS_LOADING":
      return { ...state, transactionsLoading: action.payload };
    case "SET_TRANSACTIONS_COUNT":
      return { ...state, transactionsCount: action.payload };
    case "SET_CREDITS":
      return { ...state, credits: action.payload };
    case "SET_CREDITS_LOADING":
      return { ...state, creditsLoading: action.payload };
    case "SET_TRANSACTION_OVERVIEW":
      return { ...state, transactionOverview: action.payload };
    case "SET_TRANSACTION_OVERVIEW_LOADING":
      return { ...state, transactionOverviewLoading: action.payload };
    case "SET_AVAILABLE_CREDITS":
      return { ...state, availableCredits: action.payload };
    case "SET_AVAILABLE_CREDITS_LOADING":
      return { ...state, availableCreditsLoading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    default:
      return state;
  }
};

// Custom hook for managing transaction-related states and logic
export const useTransactions = (fetchCredits: boolean = true) => {
  const [state, dispatch] = useReducer(transactionsStoreReducer, {
    transactions: [],
    transactionsLoading: false,
    transactionsCount: 0,
    credits: [],
    creditsLoading: false,
    creditsCount: 0,
    transactionOverview: null,
    transactionOverviewLoading: false,
    availableCredits: null,
    availableCreditsLoading: false,
    error: null,
  });

  const { userData, professionalGroup, isFirmAdmin } =
    useUserDataProvider();
  const {
    handleSortChange: handleSortChangeProTransactions,
    handlePageChange: handlePageChangeProTransactions,
    handleRowsPerPageChange: handleRowsPerPageChangeProTransactions,
    handleFiltersChange: handleFiltersChangeProTransactions,
    proTxnSearchAndPaginationState,
  } = useProtransactionsearch();
  const {
    handleSortChange: handleSortChangeCreditTransactions,
    handlePageChange: handlePageChangeCreditTransactions,
    handleRowsPerPageChange: handleRowsPerPageChangeCreditTransactions,
    creditTxnSearchAndPaginationState,
  } = useCreditTransactionsSearch();

  const getAdminTransactions =
    useV3GenericGetDataWithPagination<ProfessionalTransaction>(
      GenericPath.GENERIC_SUBCATEGORY_ITEM_LIST_CREATE_V3
    );

  const getAdminTransactionsOverview = useV3GenericGetData<TransactionOverview>(
    GenericPath.TRANSACTION_OVERVIEW
  );

  const getAgentTransactions =
    useV3GenericGetDataWithPagination<ProfessionalTransaction>(
      GenericPath.GENERIC_SUBCATEGORY_ITEM_RETRIEVE_UPDATE_DELETE_V3
    );

  const getAgentTransactionsOverview = useV3GenericGetData<TransactionOverview>(
    GenericPath.TRANSACTION_AGENT_OVERVIEW
  );

  const getCredits = useV3GenericGetDataWithPagination<ProfessionalTransaction>(
    GenericPath.GENERIC_SUBCATEGORY_ITEM_LIST_CREATE_V3
  );

  const getAvailableCredits = useV3GenericGetData<AvailableCredits>(
    GenericPath.AVAILABLE_CREDITS
  );

  const handleGetTransactions = useCallback(
    async (onboarded: boolean, signal?: AbortSignal) => {
      if (!userData?.ud_id || !professionalGroup?.id) return;

      try {
        dispatch({ type: "SET_TRANSACTIONS_LOADING", payload: true });

        const params = {
          ud_id: userData?.ud_id,
          entity_id: professionalGroup?.id,
          entity: "pro-group",
          subcategory: "transactions",
          subcategory_id: userData?.professional_group_role_user_data?.id,
        };
        const { filters, page, rowsPerPage, sortBy, sortDir } = proTxnSearchAndPaginationState;
        const queryParams: Record<string, any> = {
          transaction_type: filters.transaction_types || DEFAULT_TRANSACTION_TYPES,
          page: page + 1,
          page_size: rowsPerPage,
          ordering: `${sortDir === "desc" ? "-" : ""}${sortBy || "created_at"}`,
        };
        if (filters.query) queryParams.search = filters.query;
        if (filters.advisor_ud_id) queryParams.advisor_ud_id = filters.advisor_ud_id;
        if (filters.created_after) queryParams.created_after = filters.created_after;
        if (filters.created_before) queryParams.created_before = filters.created_before;
        let response;
        if (isFirmAdmin) {
          response = await getAdminTransactions(params, queryParams, signal);
        } else {
          response = await getAgentTransactions(params, queryParams, signal);
        }
          if (response?.count > 0) {
            dispatch({
              type: "SET_TRANSACTIONS",
              payload: response.data,
            });
            dispatch({
              type: "SET_TRANSACTIONS_COUNT",
              payload: response.count,
            });
          } else {
            dispatch({ type: "SET_TRANSACTIONS", payload: [] });
            dispatch({ type: "SET_TRANSACTIONS_COUNT", payload: 0 });
          }
      } catch (err) {
        dispatch({ type: "SET_ERROR", payload: err.message });
      } finally {
        dispatch({ type: "SET_TRANSACTIONS_LOADING", payload: false });
      }
    },
    [
      getAdminTransactions,
      getAgentTransactions,
      professionalGroup?.id,
      userData?.ud_id,
      isFirmAdmin,
      userData?.professional_group_role_user_data?.id,
      proTxnSearchAndPaginationState,
    ]
  );

  const handleGetCredits = useCallback(async (signal?: AbortSignal) => {
    if (!userData?.ud_id || !professionalGroup?.id) return;

    try {
      dispatch({ type: "SET_CREDITS_LOADING", payload: true });

      const params = {
        ud_id: userData?.ud_id,
        entity_id: professionalGroup?.id,
        entity: "pro-group",
        subcategory: "transactions",
      };
      const queryParams = {
        transaction_type: "CREDIT",
        page: 1, // Modify as necessary
        page_size: 25, // Modify as necessary
        ordering: "-created_at",
      };

      const response = await getCredits(params, queryParams, signal);
      if (response?.count > 0) {
        dispatch({
          type: "SET_CREDITS",
          payload: response.data,
        });
        dispatch({
          type: "SET_CREDITS_COUNT",
          payload: response.count,
        });
      } else {
        dispatch({ type: "SET_CREDITS", payload: [] });
        dispatch({ type: "SET_CREDITS_COUNT", payload: 0 });
      }
    } catch (err) {
      dispatch({ type: "SET_ERROR", payload: err.message });
    } finally {
      dispatch({ type: "SET_CREDITS_LOADING", payload: false });
    }
  }, [getCredits, professionalGroup?.id, userData?.ud_id]);

  const handleGetAvailableCredits = useCallback(async (signal?: AbortSignal) => {
    if (!userData?.ud_id || !professionalGroup?.id) return;
    
    try {
      dispatch({ type: "SET_AVAILABLE_CREDITS_LOADING", payload: true });
      
      const params = {
        ud_id: userData?.ud_id,
        entity_id: professionalGroup?.id,
        entity: "pro-group"
      };
      
      const response = await getAvailableCredits(params, { signal });
      
      dispatch({
        type: "SET_AVAILABLE_CREDITS",
        payload: response
      });
      
    } catch (err) {
      dispatch({ type: "SET_ERROR", payload: err.message });
    } finally {
      dispatch({ type: "SET_AVAILABLE_CREDITS_LOADING", payload: false });
    }
  }, [getAvailableCredits, professionalGroup?.id, userData?.ud_id]);

  const handleGetTransactionsOverview = useCallback(
    async (onboarded: boolean, signal?: AbortSignal) => {
      if (!userData?.ud_id || !professionalGroup?.id) return;

      try {
        dispatch({ type: "SET_TRANSACTION_OVERVIEW_LOADING", payload: true });

        const params = {
          ud_id: userData?.ud_id,
          entity_id: professionalGroup?.id,
          entity: "pro-group",
          subcategory: "transactions-overview",
          subcategory_id: userData?.professional_group_role_user_data?.id,
        };

          let response;
          if (isFirmAdmin) {
            response = await getAdminTransactionsOverview(params, { signal });
          } else {
            response = await getAgentTransactionsOverview(params, { signal });
          }

          dispatch({
            type: "SET_TRANSACTION_OVERVIEW",
            payload: response,
          });
      } catch (err) {
        dispatch({ type: "SET_ERROR", payload: err.message });
      } finally {
        dispatch({ type: "SET_TRANSACTION_OVERVIEW_LOADING", payload: false });
      }
    },
    [
      getAdminTransactionsOverview,
      getAgentTransactionsOverview,
      professionalGroup?.id,
      userData?.ud_id,
      isFirmAdmin,
      userData?.professional_group_role_user_data?.id
    ]
  );

  useEffect(() => {
    const abortController = new AbortController();
    if (professionalGroup?.allow_view_transactions) {
      handleGetTransactions(
        professionalGroup?.onboarding_completed_at ? true : false,
        abortController.signal
      );
      handleGetTransactionsOverview(
        professionalGroup?.onboarding_completed_at ? true : false,
        abortController.signal
      );
    }
    return () => {
      abortController.abort();
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    professionalGroup?.onboarding_completed_at,
    professionalGroup?.allow_view_transactions,
    proTxnSearchAndPaginationState,
  ]);

  useEffect(() => {
    const abortController = new AbortController();
    if (isFirmAdmin) {
      handleGetCredits(abortController.signal);
      
      // Only fetch available credits if the fetchCredits parameter is true
      if (fetchCredits) {
        handleGetAvailableCredits(abortController.signal);
      }
    }
    return () => {
      abortController.abort();
    };
  }, 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [isFirmAdmin, fetchCredits]);

  return {
    ...state,
    handleGetTransactions,
    handleGetAvailableCredits,
    handleFiltersChangeProTransactions,
    handlePageChangeProTransactions,
    handleRowsPerPageChangeProTransactions,
    handleSortChangeProTransactions,
    handlePageChangeCreditTransactions,
    handleRowsPerPageChangeCreditTransactions,
    handleSortChangeCreditTransactions,
    proTxnSearchAndPaginationState,
    creditTxnSearchAndPaginationState,
  };
};
